// types
import { Account, Loan, Payment, Transaction } from '../../../../@types';

// i18n
import { TFunction } from 'i18next';

// mui
import { Grid, Tooltip, Typography } from '@mui/material';

// table
import { BlockchainHash } from '../../../ui/BlockchainHash/BlockchainHash';
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { TableHeader, TableProps } from '../../../ui/Table/Table';

// ui
import { Amount, AmountProps } from '../../../ui/Amount/Amount';
import { Time } from '../../../ui/Time/Time';

// links
import { LinkAccount } from '../../../links/LinkAccount/LinkAccount';
import { LinkTransaction } from '../../../links/LinkTransaction/LinkTransaction';

// icons
import { RestoreOutlined as IconRestore } from '@mui/icons-material';

// props
export interface SourcesHeaderTableProps {
	frame?: TableProps['frame'];
	headersIncluded?: string[];
	t: TFunction<'translation', undefined>;
	transaction?: Transaction;
}

export const getPaymentsHeaders = ({
	frame,
	headersIncluded,
	t,
	transaction,
}: SourcesHeaderTableProps): TableHeader[] => {
	let paymentsReverted: number[] = [];

	if (transaction) {
		paymentsReverted = transaction.payments
			.filter((t) => t.revertedPayment)
			.map((t) => t.revertedPayment as number);
	}

	const columns: TableHeader[] = [];

	if (headersIncluded?.includes('transaction')) {
		columns.push({
			label: t('transaction'),
			value: 'transaction',
			render: (transaction: Transaction) => {
				return <LinkTransaction isStatic={frame === 'DIALOG'} transaction={transaction} />;
			},
		});
	}

	if (headersIncluded?.includes('source')) {
		columns.push({
			label: t('source'),
			value: 'loan',
			render: (loan: Loan) => {
				return <Typography fontWeight={600}>{(loan as Loan)?.code}</Typography>;
			},
		});
	}

	if (headersIncluded?.includes('date')) {
		columns.push({
			label: t('date'),
			value: 'transaction',
			render: (transaction: Transaction) => {
				return <Time date={transaction.date} />;
			},
		});
	}

	if (headersIncluded?.includes('recorded')) {
		columns.push({
			label: t('recorded'),
			value: 'created',
			render: (created: Date) => {
				return <Time date={created} />;
			},
		});
	}

	if (headersIncluded?.includes('hash')) {
		columns.push({
			label: t('hash'),
			value: 'blkTransaction',
			render: (
				blkTransaction: Payment['blkTransaction'],
				header: TableHeader,
				payment: Payment
			) => {
				return <BlockchainHash hash={blkTransaction.id} hasIntegrity={payment.hasIntegrity} />;
			},
		});
	}

	if (headersIncluded?.includes('status')) {
		columns.push({
			label: t('status'),
			value: 'blkTransaction',
			render: (blkTransaction: Payment['blkTransaction']) => {
				return <ChipStatus status={blkTransaction.status} />;
			},
		});
	}

	if (headersIncluded?.includes('fromAccount')) {
		columns.push({
			label: t('fromAccount'),
			value: 'transaction',
			render: (t: Transaction) => {
				return <LinkAccount account={(t?.fromAccount || transaction?.fromAccount) as Account} />;
			},
		});
	}

	if (headersIncluded?.includes('toAccount')) {
		columns.push({
			label: t('toAccount'),
			value: 'transaction',
			render: (t: Transaction) => {
				return <LinkAccount account={(t?.toAccount || transaction?.toAccount) as Account} />;
			},
		});
	}

	if (headersIncluded?.includes('amount')) {
		columns.push({
			label: transaction?.type === 'TRANSFER' ? t('sent') : t('amount'),
			value: 'amount',
			render: (amount: number, header: TableHeader, payment: Payment) => {
				const isReverted = paymentsReverted.includes(payment.id);
				const propsAmount: Partial<AmountProps> = {};

				if (isReverted) {
					propsAmount.TypographyProps = {
						sx: (theme) => {
							return {
								color: theme.palette.brand.grey[500],
							};
						},
					};
				}

				return (
					<Grid alignItems="center" container spacing={1}>
						<Grid item>
							<Amount currency={payment.currency} value={amount} {...propsAmount} />
						</Grid>
						{isReverted && (
							<Grid item>
								<Tooltip arrow={true} placement="top" title={t('reverted')}>
									<IconRestore color="error" />
								</Tooltip>
							</Grid>
						)}
					</Grid>
				);
			},
		});
	}

	if (headersIncluded?.includes('tax')) {
		columns.push({
			label: t('tax'),
			value: 'tax',
			render: (tax: number, header: TableHeader, payment: Payment) => {
				return <Amount currency={payment.currency} value={tax} />;
			},
		});
	}

	if (headersIncluded?.includes('received')) {
		columns.push({
			label: t('received'),
			value: 'amountReceived',
			render: (amount: string, header: TableHeader, item: Payment) => {
				return (
					<Amount
						currency={item.currencyReceived || item.currency}
						hasAsterisk={!item.currencyReceived || !item.amountReceived}
						value={item.amountReceived || item.amount}
					/>
				);
			},
		});
	}

	if (headersIncluded?.includes('total')) {
		columns.push({
			label: t('total'),
			value: 'amount',
			render: (amount: number, header: TableHeader, payment: Payment) => {
				return (
					<Amount currency={payment.currency} value={payment.amount + Number(payment.tax || 0)} />
				);
			},
		});
	}

	if (headersIncluded?.includes('reimbursed')) {
		columns.push({
			label: t('reimbursed'),
			value: 'reimbursed',
			render: (reimbursed: Payment['reimbursed'], header: TableHeader, payment: Payment) => {
				let currency;
				let value;

				// display reimbursed amount in transaction currency
				if (transaction) {
					currency = transaction.currency;

					if (payment.reimbursed && transaction) {
						if (payment.currency === transaction.currency) {
							value = payment.reimbursed.amount;
						} else {
							const conversion = payment.reimbursed.conversions.find(
								(c) => c.currency === transaction.currency
							);
							if (conversion) {
								value = conversion.amount;
							}
						}
					}
				}
				return <Amount currency={currency} value={value} />;
			},
		});
	}

	return columns;
};
